import React, { useState, useEffect } from "react";

import { FaSave, FaTimes, FaInfoCircle } from "react-icons/fa";
import ToggleSwitch from "../../UI/DesignStandards/Toggles/ToggleSwitch";
import {
  getPlacementRuleLabel,
  getAllowedPlacementValues,
  getRecommendedPlacementValue,
} from "../../../utility/placementRules";
import { supabase } from "../../../supabaseClient";
import { PrimaryButton, SecondaryButton } from "../../UI/DesignStandards";
import { sanitizeInput, sanitizeText } from "../../../utils/sanitization";
import AllEventConfiguration from "./AllEventConfiguration";
import EventFeeAmountFields, { filledEventFees } from "./EventFeeAmountFields";
import { countActiveTemplateInstances } from "../../../utility/squadEventInstances";
import { useTournamentId } from "../../../context/TournamentRouteContext";

const EventForm = ({
  initialData,
  onSubmit,
  onCancel,
  tournamentId: propTournamentId,
}) => {
  const routeTournamentId = useTournamentId();
  const tournamentId = propTournamentId || routeTournamentId;
  const [formData, setFormData] = useState({
    eventName: "",
    isPrimary: false,
    bowlerQuantity: 4,
    eventAbbreviation: "",
    tournamentEventCalculationTypeID: "",
    maxSharedPlayers: "2", // default recommendation for 4-person team
    gamesPerEvent: 3,
    eventScope: "tournament", // "tournament" or "squad"
  });
  const [filteredCalculationTypes, setFilteredCalculationTypes] = useState([]);
  const [calculationTypes, setCalculationTypes] = useState([]);
  const [loadingCalculationTypes, setLoadingCalculationTypes] = useState(true);
  const [allEventConfig, setAllEventConfig] = useState(null);
  const [existingAllEventConfig, setExistingAllEventConfig] = useState(null);
  const [templateInstanceCount, setTemplateInstanceCount] = useState(0);
  const [feeAmounts, setFeeAmounts] = useState({});

  useEffect(() => {
    const fetchCalculationTypes = async () => {
      try {
        setLoadingCalculationTypes(true);
        const { data, error } = await supabase
          .from("tournamentEventCalculationType")
          .select("id, name, code, description, canBePerSquad")
          .order("name");

        if (error) throw error;
        setCalculationTypes(data || []);
      } catch (error) {
        console.error("Error fetching calculation types:", error);
      } finally {
        setLoadingCalculationTypes(false);
      }
    };

    fetchCalculationTypes();
  }, []);

  // Fetch instance count for templates
  useEffect(() => {
    const fetchInstanceCount = async () => {
      if (!initialData?.isTemplate) return;
      try {
        const count = await countActiveTemplateInstances(initialData.id);
        setTemplateInstanceCount(count);
      } catch (error) {
        console.error("Error fetching instance count:", error);
      }
    };

    fetchInstanceCount();
  }, [initialData]);

  // Filter calculation types based on event scope using database metadata
  useEffect(() => {
    if (formData.eventScope === "squad") {
      // Filter to only calculation types that can be used for per-squad events
      const filtered = calculationTypes.filter(
        (type) => type.canBePerSquad === true
      );
      setFilteredCalculationTypes(filtered);

      // If current selection is not allowed for per-squad, clear it
      const selectedType = calculationTypes.find(
        (t) => t.id === formData.tournamentEventCalculationTypeID
      );
      if (selectedType && selectedType.canBePerSquad === false) {
        setFormData((prev) => ({
          ...prev,
          tournamentEventCalculationTypeID: "",
        }));
      }
    } else {
      // Tournament-wide events can use all calculation types
      setFilteredCalculationTypes(calculationTypes);
    }
  }, [
    formData.eventScope,
    calculationTypes,
    formData.tournamentEventCalculationTypeID,
  ]);

  // Fetch existing all event configuration when editing
  useEffect(() => {
    const fetchAllEventConfig = async () => {
      if (initialData?.id) {
        try {
          const { data, error } = await supabase.rpc(
            "get_all_event_configuration",
            { input_event_id: initialData.id }
          );

          if (error) {
            console.error("Error fetching all event config:", error);
            return;
          }

          if (data && data.length > 0) {
            const config = data[0];
            setExistingAllEventConfig({
              primaryEventIds: config.primary_event_ids || [],
              secondaryRuleType: config.secondary_rule_type || "",
            });
          }
        } catch (err) {
          console.error("Error fetching all event configuration:", err);
        }
      }
    };

    fetchAllEventConfig();
  }, [initialData?.id]);

  useEffect(() => {
    if (initialData) {
      // Determine event scope: if isTemplate flag is set or templateId exists, it's a template (per-squad)
      const eventScope = initialData.isTemplate || initialData.templateId ? "squad" : "tournament";

      const formDataToSet = {
        eventName: initialData.eventName || "",
        isPrimary: initialData.isPrimary || false,
        bowlerQuantity:
          initialData.isPrimary === false ? 1 : initialData.bowlerQuantity || 4,
        eventAbbreviation: initialData.eventAbbreviation || "",
        tournamentEventCalculationTypeID:
          initialData.tournamentEventCalculationTypeID || "",
        maxSharedPlayers:
          eventScope === "squad"
            ? "0" // Per-squad events default to "you can only place once"
            : initialData.maxSharedPlayers === null ||
              initialData.maxSharedPlayers === undefined
            ? "null"
            : String(initialData.maxSharedPlayers),
        gamesPerEvent:
          typeof initialData.gamesPerEvent === "number"
            ? initialData.gamesPerEvent
            : 3,
        eventScope: eventScope,
      };
      setFormData(formDataToSet);
    } else {
      // Reset form for new event creation
      const defaultFormData = {
        eventName: "",
        isPrimary: false,
        bowlerQuantity: 4,
        eventAbbreviation: "",
        tournamentEventCalculationTypeID: "",
        maxSharedPlayers: "2",
        gamesPerEvent: 3,
        eventScope: "tournament",
      };
      setFormData(defaultFormData);
    }
  }, [initialData]);

  // Note: do not force bowlerQuantity here to avoid overriding primary event values on edit

  // Keep selection valid as team size basis changes (uses 1 when not primary)
  useEffect(() => {
    const qty = Number(formData.isPrimary ? formData.bowlerQuantity : 1);
    const allowed = new Set(getAllowedPlacementValues(qty));
    const current = String(formData.maxSharedPlayers);
    const recommended = getRecommendedPlacementValue(qty);

    if (!allowed.has(current)) {
      setFormData((prev) => ({ ...prev, maxSharedPlayers: recommended }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.bowlerQuantity, formData.isPrimary]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => {
      if (name === "isPrimary") {
        const nextIsPrimary = Boolean(checked);
        return {
          ...prev,
          isPrimary: nextIsPrimary,
          bowlerQuantity: nextIsPrimary ? prev.bowlerQuantity : 1,
        };
      }

      // eventScope is now handled by ToggleSwitch onChange, not here

      return {
        ...prev,
        [name]:
          type === "checkbox"
            ? checked
            : name === "bowlerQuantity" || name === "gamesPerEvent"
            ? Number(value)
            : value,
      };
    });
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    // Sanitize text inputs only on blur
    if (name === "eventName" || name === "eventAbbreviation") {
      const sanitizedValue = sanitizeText(value);
      setFormData((prev) => ({
        ...prev,
        [name]: sanitizedValue,
      }));
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    // Validation: Cannot change event scope after creation
    if (initialData?.id) {
      const originalScope = initialData.isTemplate || initialData.templateId ? "squad" : "tournament";
      const newScope = formData.eventScope;
      
      if (originalScope !== newScope) {
        alert(
          "Event scope cannot be changed after creation. To change from tournament-wide to per-squad (or vice versa), you must delete this event and create a new one with the desired scope."
        );
        return;
      }
    }

    // Validation: Per-squad events cannot be primary events
    if (formData.eventScope === "squad" && formData.isPrimary) {
      alert(
        "Per-squad events cannot be primary events. They are always side pots."
      );
      return;
    }

    // Validation: Per-squad events cannot be all events
    const selectedCalculationType = calculationTypes.find(
      (t) => t.id === formData.tournamentEventCalculationTypeID
    );
    const isAllEvent =
      selectedCalculationType?.code === "allEventScratch" ||
      selectedCalculationType?.code === "allEventHdcp";

    if (formData.eventScope === "squad" && isAllEvent) {
      alert(
        "Per-squad events cannot be all events. All events must be tournament-wide."
      );
      return;
    }

    // Validate all event configuration if it's an all event
    if (isAllEvent && (!allEventConfig || !allEventConfig.isValid)) {
      // Validation errors will be shown by AllEventConfiguration component
      return;
    }

    // Clean up the form data before submission
    // Resolve maxSharedPlayers from UI selection
    const resolveMaxShared = () => {
      const sel = formData.maxSharedPlayers;
      if (sel === "null") return null; // no restriction
      const n = Number(sel);
      return Number.isNaN(n) ? null : n;
    };

    const cleanedFormData = {
      ...formData,
      // Include the ID if we're editing an existing event
      ...(initialData?.id && { id: initialData.id }),
      // Preserve template identification flags
      ...(initialData?.isTemplate && { isTemplate: initialData.isTemplate }),
      ...(initialData?.templateId && { templateId: initialData.templateId }),
      // Sanitize text fields
      eventName: sanitizeText(formData.eventName),
      eventAbbreviation: sanitizeText(formData.eventAbbreviation),
      tournamentEventCalculationTypeID:
        formData.tournamentEventCalculationTypeID &&
        formData.tournamentEventCalculationTypeID !== "" &&
        formData.tournamentEventCalculationTypeID !== "undefined"
          ? formData.tournamentEventCalculationTypeID
          : null,
      maxSharedPlayers: isPerSquad ? 0 : resolveMaxShared(), // Per-squad events default to 0 (you can only place once)
      gamesPerEvent: isPerSquad
        ? null
        : formData.isPrimary
        ? Number(formData.gamesPerEvent) || 3
        : null,
      // Include all event configuration if it's an all event (tournament-wide only)
      ...(!isPerSquad && isAllEvent && allEventConfig && { allEventConfig }),
      // Include event scope
      eventScope: formData.eventScope,
      ...(!initialData?.id && { fees: filledEventFees(feeAmounts) }),
    };

    onSubmit(cleanedFormData);
  };

  // Use 1 for non-primary events when computing placement text/options
  const effectiveBowlerQty = Number(
    formData.isPrimary ? formData.bowlerQuantity : 1
  );

  // Check if selected calculation type is an all event
  const selectedCalculationType = calculationTypes.find(
    (t) => t.id === formData.tournamentEventCalculationTypeID
  );
  const isAllEvent =
    selectedCalculationType?.code === "allEventScratch" ||
    selectedCalculationType?.code === "allEventHdcp";

  const isPerSquad = formData.eventScope === "squad";

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {/* Warning for templates with instances */}
      {templateInstanceCount > 0 && (
        <div className="card-inset p-4 mb-4">
          <div className="flex items-start">
            <FaInfoCircle className="w-5 h-5 text-brand-muted mt-0.5 mr-3 flex-shrink-0" />
            <div>
              <p className="text-sm font-medium text-brand-charcoal">
                This template has {templateInstanceCount} active squad instance
                {templateInstanceCount !== 1 ? "s" : ""}.
              </p>
              <p className="text-sm text-brand-muted mt-1">
                Changes to this template will automatically update all squad instances
                (except custom event names).
              </p>
            </div>
          </div>
        </div>
      )}
      {/* Primary event toggle at top */}
      <div
        className={`flex items-center p-3 rounded-lg ${
          isPerSquad ? "bg-brand-cream opacity-60" : "card-inset"
        }`}
      >
        <div className="flex items-center gap-2 w-40">
          <label
            className={`text-sm font-medium ${
              isPerSquad ? "text-brand-muted" : "text-brand-charcoal"
            }`}
          >
            Primary Event
          </label>
          <div className="group relative">
            <FaInfoCircle
              className={`w-4 h-4 cursor-help ${
                isPerSquad ? "text-brand-mist" : "text-brand-muted"
              }`}
            />
            <div className="absolute left-0 bottom-full mb-2 hidden group-hover:block z-10">
              <div className="bg-brand-charcoal text-brand-cream text-xs rounded-lg px-3 py-2 w-64 shadow-lg">
                Main tournament event. Teams must enter this event to
                participate.
                <div className="absolute left-4 top-full border-4 border-transparent border-t-brand-charcoal"></div>
              </div>
            </div>
          </div>
        </div>
        <ToggleSwitch
          checked={formData.isPrimary}
          onChange={(val) =>
            setFormData((prev) => ({
              ...prev,
              isPrimary: Boolean(val),
              bowlerQuantity: Boolean(val) ? prev.bowlerQuantity : 1,
              // When primary is enabled, force tournament-wide scope
              eventScope: Boolean(val) ? "tournament" : prev.eventScope,
            }))
          }
          size="md"
          disabled={isPerSquad}
        />
      </div>

      {/* Per Squad Event toggle */}
      <div
        className={`flex items-center p-3 rounded-lg ${
          formData.isPrimary || initialData?.id ? "bg-brand-cream opacity-60" : "card-inset"
        }`}
      >
        <div className="flex items-center gap-2 w-40">
          <label
            className={`text-sm font-medium ${
              formData.isPrimary || initialData?.id ? "text-brand-muted" : "text-brand-charcoal"
            }`}
          >
            Per Squad Event
          </label>
          <div className="group relative">
            <FaInfoCircle
              className={`w-4 h-4 cursor-help ${
                formData.isPrimary || initialData?.id ? "text-brand-mist" : "text-brand-muted"
              }`}
            />
            <div className="absolute left-0 bottom-full mb-2 hidden group-hover:block z-10">
              <div className="bg-brand-charcoal text-brand-cream text-xs rounded-lg px-3 py-2 w-64 shadow-lg">
                Creates a separate event for each squad. Always a side pot.
                <div className="absolute left-4 top-full border-4 border-transparent border-t-brand-charcoal"></div>
              </div>
            </div>
          </div>
        </div>
        <ToggleSwitch
          checked={formData.eventScope === "squad"}
          onChange={(val) =>
            setFormData((prev) => ({
              ...prev,
              eventScope: val ? "squad" : "tournament",
            }))
          }
          size="md"
          disabled={formData.isPrimary || !!initialData?.id}
        />
      </div>

      {/* Informational note about scope changes */}
      {initialData?.id && (
        <div className="p-3 card-inset">
          <p className="text-sm text-brand-muted">
            Event scope cannot be changed after creation. To change from tournament-wide to per-squad (or vice versa), delete this event and create a new one with the desired scope.
          </p>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-brand-charcoal mb-1">
            Event Name *
          </label>
          <input
            type="text"
            name="eventName"
            value={formData.eventName}
            onChange={handleChange}
            onBlur={handleBlur}
            required
            className="w-full px-3 py-2 border border-brand-charcoal/20 rounded-lg bg-white text-brand-charcoal focus:outline-none focus:ring-2 focus:ring-brand-gold-ink"
            placeholder="Enter event name"
          />
        </div>

        {/* Team Size - only for tournament-wide primary events */}
        {!isPerSquad && formData.isPrimary ? (
          <div>
            <label className="block text-sm font-medium text-brand-charcoal mb-1">
              Team Size
            </label>
            <select
              name="bowlerQuantity"
              value={formData.bowlerQuantity}
              onChange={handleChange}
              required
              className="w-full px-3 py-2 border border-brand-charcoal/20 rounded-lg bg-white text-brand-charcoal focus:outline-none focus:ring-2 focus:ring-brand-gold-ink"
            >
              <option value={1}>1 bowler</option>
              <option value={2}>2 bowlers</option>
              <option value={3}>3 bowlers</option>
              <option value={4}>4 bowlers</option>
              <option value={5}>5 bowlers</option>
            </select>
          </div>
        ) : (
          <input type="hidden" name="bowlerQuantity" value={1} />
        )}

        {/* Games Per Event - only for tournament-wide primary events */}
        {!isPerSquad && formData.isPrimary && (
          <div>
            <label className="block text-sm font-medium text-brand-charcoal mb-1">
              Games Per Event
            </label>
            <input
              type="number"
              name="gamesPerEvent"
              min={1}
              value={formData.gamesPerEvent}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-brand-charcoal/20 rounded-lg bg-white text-brand-charcoal focus:outline-none focus:ring-2 focus:ring-brand-gold-ink"
              placeholder="e.g., 3"
            />
          </div>
        )}

        <div>
          <label className="block text-sm font-medium text-brand-charcoal mb-1">
            Event Abbreviation
          </label>
          <input
            type="text"
            name="eventAbbreviation"
            value={formData.eventAbbreviation}
            onChange={handleChange}
            onBlur={handleBlur}
            className="w-full px-3 py-2 border border-brand-charcoal/20 rounded-lg bg-white text-brand-charcoal focus:outline-none focus:ring-2 focus:ring-brand-gold-ink"
            placeholder="e.g., DBL, TRP"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-brand-charcoal mb-1">
            Score Method
          </label>
          <select
            name="tournamentEventCalculationTypeID"
            value={formData.tournamentEventCalculationTypeID}
            onChange={handleChange}
            required
            disabled={loadingCalculationTypes}
            className="w-full px-3 py-2 border border-brand-charcoal/20 rounded-lg bg-white text-brand-charcoal focus:outline-none focus:ring-2 focus:ring-brand-gold-ink disabled:bg-brand-cream disabled:cursor-not-allowed"
          >
            <option value="">
              {loadingCalculationTypes ? "Loading..." : "Select a score method"}
            </option>
            {filteredCalculationTypes.map((type) => (
              <option key={type.id} value={type.id}>
                {type.name}
              </option>
            ))}
          </select>
          {formData.tournamentEventCalculationTypeID && (
            <p className="mt-1 text-sm text-brand-muted">
              {
                filteredCalculationTypes.find(
                  (t) => t.id === formData.tournamentEventCalculationTypeID
                )?.description
              }
            </p>
          )}
        </div>

        {/* Placement Rule */}
        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-brand-charcoal mb-1">
            Placement Rule
          </label>
          <select
            name="maxSharedPlayers"
            value={formData.maxSharedPlayers}
            onChange={handleChange}
            className="w-full px-3 py-2 border border-brand-charcoal/20 rounded-lg bg-white text-brand-charcoal focus:outline-none focus:ring-2 focus:ring-brand-gold-ink"
          >
            {getAllowedPlacementValues(effectiveBowlerQty).map((val) => (
              <option key={val} value={val}>
                {getPlacementRuleLabel(
                  effectiveBowlerQty,
                  val === "null" ? null : Number(val)
                )}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* All Event Configuration - only for tournament-wide events */}
      {!isPerSquad && isAllEvent && tournamentId && (
        <AllEventConfiguration
          tournamentId={tournamentId}
          initialData={existingAllEventConfig}
          onChange={setAllEventConfig}
          excludeEventId={initialData?.id}
        />
      )}

      {!initialData?.id ? (
        <EventFeeAmountFields
          amounts={feeAmounts}
          onAmountChange={(feeId, amount) =>
            setFeeAmounts((prev) => ({ ...prev, [feeId]: amount }))
          }
          events={[
            {
              name: formData.eventName || "This event",
              bowlerCount: 0,
              bowlerQuantity: formData.isPrimary ? formData.bowlerQuantity : 1,
            },
          ]}
        />
      ) : null}

      <div className="flex justify-end space-x-3 pt-4">
        <SecondaryButton type="button" onClick={onCancel}>
          <FaTimes className="mr-2 h-4 w-4" />
          Cancel
        </SecondaryButton>
        <PrimaryButton type="submit">
          <FaSave className="mr-2 h-4 w-4" />
          Save Event
        </PrimaryButton>
      </div>
    </form>
  );
};

export default EventForm;
